import { MongoClient, Db, Collection } from 'mongodb';
import { DatabasePort } from '../../domain/ports/DatabasePort';
import { injectable } from 'inversify';
import { getMongoDb, initializeMongoConnection, isMongoConnected } from '@swoft/persistence';

/**
 * MongoDB Adapter implementing DatabasePort
 * 
 * Uses @swoft/persistence for connection management while providing
 * a clean DDD interface for the domain layer.
 */
@injectable()
export class MongoDBAdapter implements DatabasePort {
  private connectionPromise: Promise<void> | null = null;

  constructor() {}

  async connect(): Promise<void> {
    // If already connected, return immediately
    if (this.isConnected()) {
      return;
    }

    // If connection is in progress, wait for it
    if (this.connectionPromise) {
      return this.connectionPromise;
    }

    // Start new connection
    this.connectionPromise = initializeMongoConnection()
      .then(() => {
        console.log('✅ MongoDB connected via MongoDBAdapter');
      })
      .catch((error) => {
        this.connectionPromise = null;
        throw error;
      });

    return this.connectionPromise;
  }

  async disconnect(): Promise<void> {
    // @swoft/persistence handles connection lifecycle
    // This is a no-op for now but keeps interface consistent
  }

  async getCollection<T>(name: string): Promise<Collection<T>> {
    // Ensure connected before getting collection
    if (!this.isConnected()) {
      await this.connect();
    }
    
    const db = getMongoDb();
    return db.collection<T>(name);
  }

  isConnected(): boolean {
    return isMongoConnected();
  }
}